/**
 * /leave-a-review — dedicated, shareable review submission page.
 *
 * Concierge use case: Rusty sends this URL to a client via SMS / email;
 * client lands directly on the form with no other reviews visible and
 * no scrolling required. The existing /reviews modal continues to work
 * unchanged for browsers who arrive via the wall.
 *
 * Renders the same shared <ReviewForm /> component the modal uses,
 * with a page-specific `renderSuccess` prop that shows a thank-you
 * card + two navigation CTAs ("Read other reviews", "Return to homepage").
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquarePlus, ArrowRight } from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import BackLink from "../components/BackLink";
import ReviewForm from "../components/reviews/ReviewForm";

export default function LeaveAReview() {
    // Track submission state so the framer-motion hero can gently fade
    // between "please share" and "thank you" copy without unmounting the
    // page shell (which would kill scroll-to-top affordance on mobile).
    const [submitted, setSubmitted] = useState(false);

    const renderSuccess = ({ reviewerName, preferredContact }) => (
        <motion.div
            suppressHydrationWarning
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="text-center py-6 md:py-10"
            data-testid="review-form-success"
            role="status"
        >
            <div className="font-serif text-4xl md:text-5xl text-[#F3EFE7] mb-4 italic">
                Thank you{reviewerName ? `, ${reviewerName}` : ""}.
            </div>
            <p className="text-[#9A958A] text-[15px] md:text-base leading-[1.85] max-w-md mx-auto mb-10">
                Your review has arrived with our concierge team. Once approved,
                it will appear on our wall of client voices at{" "}
                <span className="text-[#F3EFE7]">actluxe.net/reviews</span>.
                {preferredContact === "email"
                    ? " Check your inbox for a note of thanks."
                    : " We may follow up by phone to confirm."}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                    to="/reviews"
                    data-testid="thank-you-read-other-reviews"
                    className="inline-flex items-center gap-2 bg-[#C9A86A] hover:bg-[#D9BC80] text-[#0B0B0C] text-[11px] tracking-[0.32em] uppercase px-6 py-3 transition-colors"
                >
                    Read other reviews
                    <ArrowRight size={13} strokeWidth={1.5} />
                </Link>
                <Link
                    to="/"
                    data-testid="thank-you-return-home"
                    className="inline-flex items-center gap-2 border border-[#C9A86A]/50 text-[#C9A86A] hover:bg-[#C9A86A]/10 text-[11px] tracking-[0.32em] uppercase px-6 py-3 transition-colors"
                >
                    Return to homepage
                </Link>
            </div>
        </motion.div>
    );

    return (
        <div data-testid="leave-a-review-page">
            <SEO
                title="Leave a Review"
                description="Share your experience with ACT's smart home integration. Every review is read by our concierge team."
                canonical="https://actluxe.net/leave-a-review"
            />

            {/* Hero */}
            <section
                className="relative bg-[#0B0B0C] pt-20 md:pt-28 pb-10 md:pb-14 border-b border-[rgba(201,168,106,0.12)] overflow-hidden"
                data-testid="leave-a-review-hero"
            >
                <div className="shell">
                    <BackLink to="/reviews" label="Back to reviews" />

                    <div className="mt-6">
                        <SectionLabel number="Voice" label="Client Voice" />
                        <h1 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08] max-w-3xl">
                            {"Share your "}
                            <span className="italic text-[#C9A86A]">
                                experience.
                            </span>
                        </h1>
                        {!submitted && (
                            <p className="mt-6 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-2xl">
                                A few minutes to tell us how the install feels
                                day-to-day. All submissions are reviewed by our
                                concierge before publishing — we may reach out
                                to confirm or thank you before your review goes
                                live.
                            </p>
                        )}
                    </div>
                </div>
            </section>

            {/* Form card */}
            <section className="bg-[#0B0B0C] py-14 md:py-20">
                <div className="shell">
                    <motion.div
                        suppressHydrationWarning
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.6,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="max-w-2xl mx-auto bg-[#15161A] border border-[rgba(201,168,106,0.25)] p-6 md:p-10 lg:p-12"
                    >
                        <div className="flex items-center gap-3 mb-8">
                            <MessageSquarePlus
                                size={16}
                                strokeWidth={1.5}
                                className="text-[#C9A86A]"
                                aria-hidden="true"
                            />
                            <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                Leave a review
                            </span>
                        </div>
                        <ReviewForm
                            onSuccess={() => setSubmitted(true)}
                            renderSuccess={renderSuccess}
                        />
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
